import dayjs from "dayjs"
import { type ReactNode } from "react"

import { type ClassSessionConstructor } from "#customTypes/composedTypes/classSessionTypes"
import { type ClassConstructor } from "#customTypes/composedTypes/classTypes"
import { type StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import {
  type TryoutConstructor,
  type TryoutForTable,
} from "#customTypes/composedTypes/tryoutTypes"
import { type Uuid } from "#customTypes/globalTypes"
import { STANDARD_DATE_FORMAT, STANDARD_TIME_FORMAT } from "#utils/dateHelpers"
import { formatSemester } from "#utils/semesterHelpers"
import {
  getProgressIndicator,
  getTryoutResult,
  TRYOUT_RESULT_SORT_RANK,
  type TryoutResult,
} from "#utils/tryoutHelpers"

import { Student } from "./Student"

export class Tryout {
  id: Uuid
  studentId: Uuid
  sessionId: Uuid
  tryoutFilterDate: string
  createdAt: Date
  semester: string | undefined
  progress: ReactNode
  result: TryoutResult

  constructor(props: TryoutConstructor) {
    this.id = props.id
    this.studentId = props.studentId
    this.sessionId = props.sessionId
    this.tryoutFilterDate = props.tryoutFilterDate
    this.createdAt = new Date(props.createdAt)
    this.semester = props.semester
    this.progress = getProgressIndicator(props)
    this.result = getTryoutResult(props.step2?.didPassTryout)
  }

  getFormattedCreatedAt = (): string => this.createdAt?.toLocaleString() ?? ""

  getTryoutForTable = (
    student: StudentConstructor,
    classObj: ClassConstructor,
    classSessionObj: ClassSessionConstructor
  ): TryoutForTable => {
    const times = classSessionObj
      ? {
          startDate: dayjs(classSessionObj.eventStart).format(
            STANDARD_DATE_FORMAT
          ),
          startTime: dayjs(classSessionObj.eventStart).format(
            STANDARD_TIME_FORMAT
          ),
          endTime: dayjs(classSessionObj.eventEnd).format(STANDARD_TIME_FORMAT),
          eventStart: dayjs(classSessionObj.eventStart).valueOf(),
        }
      : {
          startDate: "Unknown",
          startTime: "Unknown",
          endTime: "Unknown",
          eventStart: 0,
        }

    return {
      ...times,
      id: this.id,
      // studentName stays the plain name for sorting/searching by name;
      // studentNameAndDob is the DOB-prefixed string kept for search.
      // studentDob is the separate display column.
      studentName: student?.name ?? "Unknown student",
      studentNameAndDob: student
        ? new Student(student).getNameAndDob()
        : "Unknown student",
      studentDob: student ? new Student(student).getDobString() : "",
      className: classObj?.name ?? "Unknown class",
      createdAt: this.getFormattedCreatedAt(),
      semester: formatSemester(this.semester),
      progress: this.progress,
      result: this.result,
      resultRank: TRYOUT_RESULT_SORT_RANK[this.result],
    }
  }
}
